/**
 * Account-scoped click-through acceptance state for the consent gate.
 *
 * The click can happen before sign-in, so every fresh acceptance starts unclaimed in `pending`. Only an
 * account verified against restored Firebase auth claims it for replay; after that, each account keeps its local
 * acceptance and can never expose its timestamp to another account. Pre-scoping V2 records carry no
 * trustworthy owner and are deliberately invalidated (owner ruling, 2026-08-22).
 *
 * The unclaimed click also remembers which account the page showed as signed in when it was made
 * (`clickedBy`). A click made while signed out stays claimable by whichever account signs in next; a click
 * made while account A was signed in is kept for A only, so no other account can claim it, send its
 * timestamp, or have its prompt hidden by it (api#780, owner decision 2026-09-14). That tag is the page's
 * cached account and is never trusted on its own: A still claims only after Firebase verifies A, so the
 * tag can only narrow who may claim, never widen it. There is one unclaimed slot, so when a newer click
 * replaces A's unsent click, A's click moves to A's own entry, still pending, instead of being erased; the
 * replay funnel verifies A before sending an account entry exactly as it does for the unclaimed slot.
 */

import {browserLocalStorage} from "../core/browserStorage";

/**
 * The oldest Terms version an acceptance may name and still count. Bump it to a new Terms' "Last updated"
 * date when that change must be accepted again: every acceptance of an older version stops counting, so
 * the gate asks once more.
 *
 * It is NOT the version a click records. That is the version the api announces at the click, or the
 * bundle's own when the lookup gives none (`./acceptedTermsVersion`, kitecorp/transferify-ui#3485), so a
 * Terms update reaches the record without anybody editing this file, and only a change that must be
 * re-accepted moves it.
 */
export const OLDEST_ACCEPTED_TERMS_VERSION = "2026-07-10";

/** A Terms version's shape: its "Last updated" date, the only shape the api's acceptance record takes. */
export const TERMS_VERSION_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Account-scoped storage introduced for api#780. */
export const TERMS_ACCEPTED_KEY = "TERMS_ACCEPTED_V3";
/** Unowned record written before account scoping; never migrate it to an account. */
export const LEGACY_TERMS_ACCEPTED_KEY = "TERMS_ACCEPTED_V2";
const UNVERSIONED_TERMS_ACCEPTED_KEY = "TERMS_ACCEPTED";

export type AcceptanceStatus = "pending" | "recorded";

export interface AcceptanceRecord {
	/**
	 * The Terms version the click accepted (`./acceptedTermsVersion`). Null only on a click stored before every
	 * click named a version; such a click still counts by its date but is never replayed.
	 */
	version: string | null;
	acceptedAt: string;
	status: AcceptanceStatus;
}

/**
 * A claim that moved the unclaimed click remembers who clicked, so a release can restore exactly that
 * record; a claim of an existing account entry has nothing to restore.
 */
export type AcceptanceClaim =
	| {acceptance: AcceptanceRecord; movedFromPending: false}
	| {acceptance: AcceptanceRecord; movedFromPending: true; clickedBy: string | null};

interface AccountAcceptance {
	accountId: string;
	record: AcceptanceRecord;
}

/** The unclaimed click; `clickedBy` is null for a click made while signed out. */
interface PendingClick {
	record: AcceptanceRecord;
	clickedBy: string | null;
}

interface TermsAcceptanceState {
	pending: PendingClick | null;
	accounts: AccountAcceptance[];
}

type TermsListener = () => void;

const EMPTY_STATE: TermsAcceptanceState = {pending: null, accounts: []};
const listeners = new Set<TermsListener>();
let sessionState: TermsAcceptanceState | null = null;

function copyRecord(record: AcceptanceRecord): AcceptanceRecord {
	return {...record};
}

function copyState(state: TermsAcceptanceState): TermsAcceptanceState {
	return {
		pending: state.pending === null ? null : {record: copyRecord(state.pending.record), clickedBy: state.pending.clickedBy},
		accounts: state.accounts.map(({accountId, record}) => ({accountId, record: copyRecord(record)})),
	};
}

/** Stored flat, so a signed-out click keeps exactly the shape the previous release wrote. */
function storedState(state: TermsAcceptanceState): unknown {
	const {pending} = state;
	return {
		pending: pending === null
			? null
			: pending.clickedBy === null ? pending.record : {...pending.record, clickedBy: pending.clickedBy},
		accounts: state.accounts,
	};
}

/**
 * Whether an acceptance still counts: it names a version no older than {@link OLDEST_ACCEPTED_TERMS_VERSION},
 * or, naming none, was clicked on or after that version's date. Both are policy dates, so they compare as
 * strings.
 */
function stillCounts(version: string | null, acceptedAt: string): boolean {
	const acceptedTerms = version ?? new Date(Date.parse(acceptedAt)).toISOString().slice(0, 10);
	return acceptedTerms >= OLDEST_ACCEPTED_TERMS_VERSION;
}

function recordFrom(raw: unknown): AcceptanceRecord | null {
	if (typeof raw !== "object" || raw === null) {
		return null;
	}
	const {version, acceptedAt, status} = raw as Partial<AcceptanceRecord>;
	if ((version !== null && (typeof version !== "string" || !TERMS_VERSION_PATTERN.test(version)))
		|| typeof acceptedAt !== "string"
		|| Number.isNaN(Date.parse(acceptedAt))
		|| (status !== "pending" && status !== "recorded")
		|| !stillCounts(version, acceptedAt)) {
		return null;
	}
	return {version, acceptedAt, status};
}

/** A record without `clickedBy` was written before the clicking account was kept, and reads as signed out. */
function pendingFrom(raw: unknown): PendingClick | null {
	const record = recordFrom(raw);
	if (record === null) {
		return null;
	}
	const {clickedBy} = raw as {clickedBy?: unknown};
	if (clickedBy === undefined) {
		return {record, clickedBy: null};
	}
	return typeof clickedBy === "string" && clickedBy.length > 0 ? {record, clickedBy} : null;
}

function stateFrom(raw: unknown): TermsAcceptanceState | null {
	if (typeof raw !== "object" || raw === null) {
		return null;
	}
	const value = raw as {pending?: unknown; accounts?: unknown};
	if (!Array.isArray(value.accounts)) {
		return null;
	}
	const pending = value.pending === null ? null : pendingFrom(value.pending);
	if (value.pending !== null && pending === null) {
		return null;
	}
	const accounts: AccountAcceptance[] = [];
	const seen = new Set<string>();
	for (const rawAccount of value.accounts) {
		if (typeof rawAccount !== "object" || rawAccount === null) {
			return null;
		}
		const {accountId, record: rawRecord} = rawAccount as {accountId?: unknown; record?: unknown};
		const record = recordFrom(rawRecord);
		if (typeof accountId !== "string" || accountId.length === 0 || record === null) {
			return null;
		}
		if (!seen.has(accountId)) {
			accounts.push({accountId, record});
			seen.add(accountId);
		}
	}
	return {pending, accounts};
}

function clearLegacy(storage: Storage): void {
	storage.removeItem(LEGACY_TERMS_ACCEPTED_KEY);
	storage.removeItem(UNVERSIONED_TERMS_ACCEPTED_KEY);
}

function readState(): TermsAcceptanceState {
	const storage = browserLocalStorage();
	if (storage === null) {
		return sessionState === null ? copyState(EMPTY_STATE) : copyState(sessionState);
	}
	try {
		clearLegacy(storage);
		if (sessionState !== null) {
			storage.setItem(TERMS_ACCEPTED_KEY, JSON.stringify(storedState(sessionState)));
			const recovered = copyState(sessionState);
			sessionState = null;
			return recovered;
		}
		const stored = storage.getItem(TERMS_ACCEPTED_KEY);
		if (stored === null) {
			return copyState(EMPTY_STATE);
		}
		let parsed: unknown;
		try {
			parsed = JSON.parse(stored);
		} catch {
			storage.removeItem(TERMS_ACCEPTED_KEY);
			return copyState(EMPTY_STATE);
		}
		const state = stateFrom(parsed);
		if (state === null) {
			storage.removeItem(TERMS_ACCEPTED_KEY);
			return copyState(EMPTY_STATE);
		}
		return state;
	} catch {
		return sessionState === null ? copyState(EMPTY_STATE) : copyState(sessionState);
	}
}

function writeState(state: TermsAcceptanceState): void {
	const next = copyState(state);
	sessionState = next;
	const storage = browserLocalStorage();
	if (storage !== null) {
		try {
			clearLegacy(storage);
			storage.setItem(TERMS_ACCEPTED_KEY, JSON.stringify(storedState(next)));
			sessionState = null;
		} catch {
			// The page-session state remains authoritative when durable storage is unavailable.
		}
	}
	listeners.forEach((listener) => listener());
}

function sameAcceptance(left: AcceptanceRecord, right: AcceptanceRecord): boolean {
	return left.version === right.version && left.acceptedAt === right.acceptedAt;
}

/**
 * Moves another account's unsent click out of the unclaimed slot before a newer click takes it, so that
 * account still sends it on its next verified sign-in. An account that already has an entry keeps that
 * entry, which already accepts the current terms for it.
 */
function keepReplacedClick(state: TermsAcceptanceState, clickedBy: string | null): void {
	const replaced = state.pending;
	if (replaced === null || replaced.clickedBy === null || replaced.clickedBy === clickedBy) {
		return;
	}
	const owner = replaced.clickedBy;
	if (!state.accounts.some((entry) => entry.accountId === owner)) {
		state.accounts.push({accountId: owner, record: replaced.record});
	}
}

/** The acceptance that answers this account's gate, or, signed out (null), the unclaimed click. */
function recordFor(state: TermsAcceptanceState, accountId: string | null): AcceptanceRecord | null {
	if (accountId === null) {
		return state.pending === null ? null : copyRecord(state.pending.record);
	}
	const owned = state.accounts.find((entry) => entry.accountId === accountId)?.record;
	const record = owned ?? pendingVisibleTo(state, accountId)?.record;
	return record === undefined ? null : copyRecord(record);
}

/**
 * Whether raw stored state (the parsed `TERMS_ACCEPTED_V3` value) answers the gate for this account, or for
 * a signed-out page (null), by the same rules as {@link TermsStorage.retrieve}. Exported for the e2e
 * harness, which reads a page's stored state and must judge it by these rules rather than a copy of them.
 */
export function storedStateAccepts(raw: unknown, accountId: string | null): boolean {
	const state = stateFrom(raw);
	return state !== null && recordFor(state, accountId) !== null;
}

/** The unclaimed click this account may see: one made while signed out, or its own. */
function pendingVisibleTo(state: TermsAcceptanceState, accountId: string): PendingClick | null {
	const {pending} = state;
	return pending !== null && (pending.clickedBy === null || pending.clickedBy === accountId) ? pending : null;
}

export default class TermsStorage {
	/**
	 * Records a click as unclaimed until the replay funnel verifies Firebase and claims it. `clickedBy` is
	 * the account signed in when the visitor clicked, or null when signed out; it is required so no caller
	 * can make a signed-in click claimable by every account by leaving it out. `version` is the Terms
	 * version the click accepted (`./acceptedTermsVersion`: the one the api announced, or the bundle's own
	 * when the lookup gave none); required for the same reason, so no caller can pick a version of its own. `clickedAt` is the
	 * moment of the click, which a click held for the announcement passes in rather than taking the moment
	 * the announcement answered.
	 */
	static save(clickedBy: string | null, version: string | null, clickedAt: Date = new Date()): void {
		const state = readState();
		keepReplacedClick(state, clickedBy);
		state.pending = {
			record: {
				version,
				acceptedAt: clickedAt.toISOString(),
				status: "pending",
			},
			clickedBy,
		};
		writeState(state);
	}

	static retrieve(accountId: string | null = null): boolean {
		return TermsStorage.retrieveRecord(accountId) !== null;
	}

	/**
	 * Returns this account's acceptance, or the unclaimed click it may see. Signed out (null), the unclaimed
	 * click is returned whoever made it: there is no account to keep it from, and the signed-in gates and
	 * the replay funnel apply the account check.
	 */
	static retrieveRecord(accountId: string | null = null): AcceptanceRecord | null {
		return recordFor(readState(), accountId);
	}

	/**
	 * A pending owned record or visible unclaimed click worth verifying before any ownership mutation. An
	 * owned click that names no version is not: it has nothing to send and nobody else to be claimed from,
	 * so it would cost a Firebase check on every store change for nothing.
	 */
	static pendingForReplay(accountId: string): AcceptanceRecord | null {
		const state = readState();
		const owned = state.accounts.find((entry) => entry.accountId === accountId)?.record;
		if (owned?.status === "pending" && owned.version !== null) {
			return copyRecord(owned);
		}
		const visible = pendingVisibleTo(state, accountId);
		return visible === null ? null : copyRecord(visible.record);
	}

	/** Atomically moves the unclaimed click this account may see to that verified account. */
	static claimForReplay(accountId: string): AcceptanceClaim | null {
		const state = readState();
		const owned = state.accounts.find((entry) => entry.accountId === accountId)?.record;
		const visible = pendingVisibleTo(state, accountId);
		if (owned !== undefined) {
			if (visible !== null) {
				state.pending = null;
				writeState(state);
			}
			return {acceptance: copyRecord(owned), movedFromPending: false};
		}
		if (visible === null) {
			return null;
		}
		state.pending = null;
		state.accounts.push({accountId, record: visible.record});
		writeState(state);
		return {acceptance: copyRecord(visible.record), movedFromPending: true, clickedBy: visible.clickedBy};
	}

	/**
	 * Returns only a just-moved click to the unclaimed slot, tagged with the account that clicked, so a
	 * release never makes a signed-in click claimable by another account. Established account ownership is
	 * never weakened.
	 */
	static releaseClaim(accountId: string, claim: AcceptanceClaim): void {
		if (!claim.movedFromPending) {
			return;
		}
		const state = readState();
		const owned = state.accounts.find((entry) => entry.accountId === accountId);
		if (owned === undefined || !sameAcceptance(owned.record, claim.acceptance)) {
			return;
		}
		if (state.pending !== null && claim.clickedBy === accountId) {
			// A newer click holds the slot; the account's own click stays in its entry, as keepReplacedClick leaves it.
			return;
		}
		state.accounts = state.accounts.filter((entry) => entry !== owned);
		if (state.pending === null) {
			state.pending = {record: owned.record, clickedBy: claim.clickedBy};
		}
		writeState(state);
	}

	static markRecorded(accountId: string, acceptance: AcceptanceRecord): void {
		const state = readState();
		const owned = state.accounts.find((entry) => entry.accountId === accountId);
		if (owned === undefined || !sameAcceptance(owned.record, acceptance)) {
			return;
		}
		owned.record = {...owned.record, status: "recorded"};
		writeState(state);
	}

	static discard(accountId: string, acceptance: AcceptanceRecord): void {
		const state = readState();
		state.accounts = state.accounts.filter((entry) =>
			entry.accountId !== accountId || !sameAcceptance(entry.record, acceptance));
		writeState(state);
	}

	/** Synchronous same-tab publication; browser `storage` events do not fire in the writing tab. */
	static subscribe(listener: TermsListener): () => void {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	}
}
